import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  List,
  ListItem,
  ListItemText,
  Typography,
  Paper,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SidePanel from "../../components/SidePanel";
import TopBar from "../../components/TopBar";
import { userService } from "../../apis/users";
import { User, UserResponse, CarType, DriverType } from "../../types/users";
import {
  formatTextToCapitalise,
  formatTime,
  getDriverTypes,
  textCapitalise,
} from "../../utils/util";
import { jwtDecode } from "jwt-decode";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { APIProvider, Map, Marker, InfoWindow } from "@vis.gl/react-google-maps";
import normalCar from "../../assets/images/normal_car.png";
import normalVan from "../../assets/images/normal_van.png";
import normalBike from "../../assets/images/normal_bike.png";
import normalTruck from "../../assets/images/normal_truck.png";
import normalSharedCar from "../../assets/images/normal_shared_car.png";
import teracarCar from "../../assets/images/teracar_car.png";
import teracarVan from "../../assets/images/teracar_van.png";
import teracarBike from "../../assets/images/teracar_bike.png";
import { getCountriesData } from "../../apis/master";

interface Location {
  lat: number;
  lng: number;
}

interface DeliveryCapacity {
  max_weight: number;
  max_width: number;
  max_height: number;
  max_length: number;
}

interface Client {
  clientId: string;
  clientRole: string;
  ownerName?: string;
  carType?: string;
  email?: string;
  phoneNumber?: string;
  acceptSharedRides?: boolean;
  acceptDeliveries?: boolean;
  isPremiumVtc?: boolean;
  isCommunityVtc?: boolean;
  driverType?: string;
  deliveryCapacity?: DeliveryCapacity;
  isAvailable?: boolean;
  isOngoingRide?: boolean;
  location?: {
    latitude: number;
    longitude: number;
  };
  connectedTime?: string;
}

interface Event {
  event: string;
  client: Client;
  error: string;
  connections: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface DriverLocation {
  driverId: string;
  location: Location;
  color: string;
}

interface MapViewProps {
  drivers: User[];
  connectedClients: Client[];
  selectedRegion?: any;
}

// Add this interface for the custom marker
interface CustomMarkerProps {
  position: Location;
  label: string;
  color: string;
  onClick: () => void;
  driver: User;
  isOnline?: boolean;
}

// Add the CustomMarker component
const CustomMarker: React.FC<CustomMarkerProps> = ({ position, label, color, onClick, driver }) => {
  const getMarkerIcon = () => {
    const isShared = driver.driverType === "professional" || driver.accept_shared_rides;

    if (!driver.carType) {
      return isShared ? normalSharedCar : driver.is_teracar_driver ? teracarCar : normalCar;
    }

    switch (driver.carType) {
      case "VAN":
        return driver.is_teracar_driver ? teracarVan : normalVan;
      case "MOTOR_BIKE":
        return driver.is_teracar_driver ? teracarBike : normalBike;
      case "MEDIUM_TRUCK":
        return driver.is_teracar_driver ? normalTruck : normalTruck;
      case "LARGE_TRUCK":
        return driver.is_teracar_driver ? normalTruck : normalTruck;
      case "TRIKE":
        return driver.is_teracar_driver ? normalTruck : normalTruck;
      case "GREEN":
      case "CLASSIC":
      default:
        return driver.is_teracar_driver ? teracarCar : isShared ? normalSharedCar : normalCar;
    }
  };

  // Format lastLocationUpdated for display with time
  const formatLastLocationTime = () => {
    if (!driver.lastLocationUpdated) return "";
    try {
      const date = new Date(driver.lastLocationUpdated);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'Min' : 'Mins'} ago`;
      if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'Hour' : 'Hours'} ago`;
      if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'Day' : 'Days'} ago`;
      // Show date and time for older entries
      return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return "";
    }
  };

  const lastLocationTime = formatLastLocationTime();
  
  // Combine driver name and Last Online time on separate lines
  const labelText = lastLocationTime 
    ? `${label}\nLast Online: ${lastLocationTime}`
    : label;

  return (
    <Marker
      position={position}
      onClick={onClick}
      label={{
        text: labelText,
        color: "#333333", // Dark gray color
        fontSize: "12px",
        fontWeight: "500",
        className: "custom-marker-label",
      }}
      icon={{
        url: getMarkerIcon(),
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16),
      }}
    />
  );
};

// Add styles for the custom marker label
const styles = `
  .custom-marker-label {
    background-color: rgba(255, 255, 255, 0.9);
    padding: 4px 8px;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    white-space: pre-line;
    min-width: 100px;
    text-align: center;
    transform: translateY(-30px);
    font-family: Roboto, Arial, sans-serif;
    line-height: 1.4;
    color: #333333;
  }
`;

const DEBOUNCE_DELAY = 60000; // 2 seconds delay for location updates
const WEBSOCKET_REFRESH_INTERVAL = 300000; // 5 minutes

const MapView: React.FC<MapViewProps> = ({ drivers, connectedClients, selectedRegion }) => {
  const [selectedDriver, setSelectedDriver] = useState<User | null>(null);
  const [driverLocations, setDriverLocations] = useState<DriverLocation[]>([]);
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(0);
  const [lastProcessedClients, setLastProcessedClients] = useState<Set<string>>(new Set());

  // Get color based on driver type
  const getDriverColor = (driverType?: string | null) => {
    switch (driverType) {
      case "delivery":
        return "#FF0000"; // Red for delivery drivers
      case "professional":
        return "#0000FF"; // Blue for professional/shared drivers
      default:
        return "#00FF00"; // Green for normal drivers
    }
  };

  // Validate if a location object has valid coordinates
  const isValidLocation = (location: any): location is { latitude: number; longitude: number } => {
    console.log("Validating location:", location);

    if (!location) {
      console.log("Location is null or undefined");
      return false;
    }

    // If location is a string, try to parse it
    let parsedLocation = location;
    if (typeof location === "string") {
      try {
        parsedLocation = JSON.parse(location);
        console.log("Parsed location string:", parsedLocation);
      } catch (e) {
        console.log("Failed to parse location string:", e);
        return false;
      }
    }

    if (typeof parsedLocation !== "object") {
      console.log("Location is not an object, type:", typeof parsedLocation);
      return false;
    }

    if (typeof parsedLocation.latitude !== "number") {
      console.log(
        "Latitude is not a number, value:",
        parsedLocation.latitude,
        "type:",
        typeof parsedLocation.latitude
      );
      return false;
    }

    if (typeof parsedLocation.longitude !== "number") {
      console.log(
        "Longitude is not a number, value:",
        parsedLocation.longitude,
        "type:",
        typeof parsedLocation.longitude
      );
      return false;
    }

    if (isNaN(parsedLocation.latitude)) {
      console.log("Latitude is NaN");
      return false;
    }

    if (isNaN(parsedLocation.longitude)) {
      console.log("Longitude is NaN");
      return false;
    }

    if (parsedLocation.latitude < -90 || parsedLocation.latitude > 90) {
      console.log("Latitude out of range:", parsedLocation.latitude);
      return false;
    }

    if (parsedLocation.longitude < -180 || parsedLocation.longitude > 180) {
      console.log("Longitude out of range:", parsedLocation.longitude);
      return false;
    }

    console.log("Location is valid:", parsedLocation);
    return true;
  };

  // Remove duplicates and filter only drivers with valid locations
  const processClients = useCallback((clients: Client[]) => {
    // Create a Map to store unique clients by clientId using reduce
    const uniqueClientsMap = clients.reduce((acc: { [key: string]: Client }, client) => {
      if (client.clientRole === "DRIVER" && client.location) {
        acc[client.clientId] = client;
      }
      return acc;
    }, {});

    const uniqueClientsArray = Object.values(uniqueClientsMap);
    console.log("Unique clients after deduplication:", uniqueClientsArray);
    return uniqueClientsArray;
  }, []);

  // Check if locations have actually changed
  const hasLocationChanges = useCallback(
    (newLocations: DriverLocation[], oldLocations: DriverLocation[]) => {
      if (newLocations.length !== oldLocations.length) {
        console.log("Number of locations changed:", {
          old: oldLocations.length,
          new: newLocations.length,
        });
        return true;
      }

      const changes = newLocations.some((newLoc) => {
        const oldLoc = oldLocations.find((loc) => loc.driverId === newLoc.driverId);
        if (!oldLoc) return true;

        const hasChanged =
          oldLoc.location.lat !== newLoc.location.lat ||
          oldLoc.location.lng !== newLoc.location.lng;

        if (hasChanged) {
          console.log("Location changed for driver:", newLoc.driverId, {
            old: oldLoc.location,
            new: newLoc.location,
          });
        }

        return hasChanged;
      });

      return changes;
    },
    []
  );

  // Debounced update function
  const debouncedUpdateLocations = useCallback(
    (clients: Client[]) => {
      const now = Date.now();
      if (now - lastUpdateTime < DEBOUNCE_DELAY) {
        console.log("Skipping update - too soon since last update");
        return;
      }

      // Process and deduplicate clients
      const uniqueClients = processClients(clients);

      // Create a set of current client IDs for comparison
      const currentClientIds = new Set<string>(uniqueClients.map((client) => client.clientId));

      // Check if the set of clients has changed
      const clientsChanged =
        currentClientIds.size !== lastProcessedClients.size ||
        !Array.from(currentClientIds).every((id) => lastProcessedClients.has(id));

      if (!clientsChanged) {
        console.log("No change in connected clients, skipping update");
        return;
      }

      console.log("Processing location update after debounce", {
        totalClients: clients.length,
        uniqueClients: uniqueClients.length,
        previousClients: lastProcessedClients.size,
      });

      setLastUpdateTime(now);
      setLastProcessedClients(currentClientIds);

      const updatedLocations = (uniqueClients as Client[])
        .filter(
          (client): client is Client & { location: { latitude: number; longitude: number } } => {
            // Parse location if it's a string
            let location = client.location;
            if (typeof location === "string") {
              try {
                location = JSON.parse(location);
              } catch (e) {
                console.log("Failed to parse location for driver", client.clientId, e);
                return false;
              }
            }

            if (!location || !isValidLocation(location)) {
              console.log("Invalid location data for driver", client.clientId);
              return false;
            }
            return true;
          }
        )
        .map((client) => {
          const driver = drivers.find((d) => d._id === client.clientId);
          if (!driver) {
            console.log("No driver data found for client", client.clientId);
            return null;
          }

          // Parse location if it's a string
          let location = client.location;
          if (typeof location === "string") {
            location = JSON.parse(location);
          }

          const transformedLocation: Location = {
            lat: Number(location.latitude),
            lng: Number(location.longitude),
          };

          return {
            driverId: client.clientId,
            location: transformedLocation,
            color: getDriverColor(client.driverType),
          };
        })
        .filter((loc): loc is NonNullable<typeof loc> => loc !== null);

      // Only update if locations have actually changed
      if (hasLocationChanges(updatedLocations, driverLocations)) {
        console.log("Updating map with new locations:", updatedLocations);
        setDriverLocations(updatedLocations);
      } else {
        console.log("No location changes detected, skipping map update");
      }
    },
    [
      lastUpdateTime,
      drivers,
      driverLocations,
      lastProcessedClients,
      processClients,
      hasLocationChanges,
    ]
  );

  // Update driver locations when connected clients change
  useEffect(() => {
    debouncedUpdateLocations(connectedClients);
  }, [connectedClients, debouncedUpdateLocations]);

  // Process offline drivers with location data
  useEffect(() => {
    if (!drivers || drivers.length === 0) {
      console.log("No drivers to process");
      return;
    }

    const onlineDriverIds = new Set(connectedClients.map((client) => client.clientId));
    console.log("Processing offline drivers:", {
      totalDrivers: drivers.length,
      onlineDrivers: onlineDriverIds.size,
      connectedClients: connectedClients.length,
    });
    
    // Find offline drivers that have location data
    const offlineDriversWithLocations = drivers.filter((driver) => {
      // Skip if driver is online (will be handled by WebSocket updates)
      if (onlineDriverIds.has(driver._id)) {
        return false;
      }
      // Include if driver has location data
      const hasLocation = driver.location && 
             typeof driver.location.latitude === 'number' && 
             typeof driver.location.longitude === 'number' &&
             !isNaN(driver.location.latitude) &&
             !isNaN(driver.location.longitude);
      
      if (!hasLocation) {
        console.log("Driver has no valid location:", driver._id, driver.firstName, driver.lastName);
      }
      
      return hasLocation;
    });

    console.log("Offline drivers with locations:", offlineDriversWithLocations.length);

    // Convert offline drivers to DriverLocation format
    const offlineLocations: DriverLocation[] = offlineDriversWithLocations.map((driver) => ({
      driverId: driver._id,
      location: {
        lat: Number(driver.location!.latitude),
        lng: Number(driver.location!.longitude),
      },
      color: getDriverColor(driver.driverType),
    }));

    // Merge offline locations with existing driverLocations
    // Online drivers (from WebSocket) take precedence
    setDriverLocations((prevLocations) => {
      console.log("Merging locations:", {
        previousLocations: prevLocations.length,
        offlineLocations: offlineLocations.length,
      });

      // Create a map of existing locations (prioritize online drivers)
      const locationMapObj: { [key: string]: DriverLocation } = {};
      
      // First, add all existing locations (these are online drivers from WebSocket)
      prevLocations.forEach((loc) => {
        locationMapObj[loc.driverId] = loc;
      });
      
      // Then, add offline drivers only if they're not already in the map (not online)
      offlineLocations.forEach((loc) => {
        if (!locationMapObj[loc.driverId]) {
          locationMapObj[loc.driverId] = loc;
        }
      });
      
      const mergedLocations = Object.values(locationMapObj);
      
      console.log("Merged locations:", mergedLocations.length);
      
      // Only update if there are actual changes
      if (hasLocationChanges(mergedLocations, prevLocations)) {
        console.log("Updating map with offline driver locations:", offlineLocations.length);
        return mergedLocations;
      }
      
      console.log("No changes detected, keeping previous locations");
      return prevLocations;
    });
  }, [drivers, connectedClients, hasLocationChanges]);

  // Dynamic map center based on selected region
  const mapCenter = useMemo(() => {
    if (selectedRegion) {
      return {
        lat: selectedRegion.lat,
        lng: selectedRegion.lng,
      };
    }
    // Fallback to Abidjan if no region is selected
    return {
      lat: 5.3599, // Abidjan latitude
      lng: -4.0083, // Abidjan longitude
    };
  }, [selectedRegion]);

  // Calculate driver counts by type
  const getDriverTypeCounts = useCallback(() => {
    const counts = {
      delivery: { total: 0, bike: 0, car: 0, truck: 0, trike: 0 },
      professional: { total: 0, classic: 0, green: 0, van: 0 },
      normal: { total: 0, classic: 0, green: 0, van: 0 },
    };

    driverLocations.forEach((location) => {
      const driver = drivers.find((d) => d._id === location.driverId);
      if (driver?.driverType) {
        if (driver.driverType === "professional") {
          counts.professional.total++;
          if (driver.carType === "CLASSIC") {
            counts.professional.classic++;
          } else if (driver.carType === "GREEN") {
            counts.professional.green++;
          } else if (driver.carType === "VAN") {
            counts.professional.van++;
          }
        } else if (driver.driverType === "delivery") {
          counts.delivery.total++;
          if (driver.carType === "MOTOR_BIKE") {
            counts.delivery.bike++;
          } else if (
            driver.carType === "MEDIUM_TRUCK" ||
            driver.carType === "LARGE_TRUCK" ||
            driver.carType === "TRIKE"
          ) {
            counts.delivery.truck++;
          } else {
            counts.delivery.car++;
          }
        } else {
          // For normal drivers, we'll count them as delivery if they accept delivery rides
          // For normal drivers, we'll count them as shared if they accept shared rides
          if (driver.accept_shared_rides) {
            counts.professional.total++;
            if (driver.carType === "CLASSIC") {
              counts.professional.classic++;
            } else if (driver.carType === "GREEN") {
              counts.professional.green++;
            } else if (driver.carType === "VAN") {
              counts.professional.van++;
            }
          } else if (driver.accept_deliveries) {
            counts.delivery.total++;
            if (
              driver.carType === "CLASSIC" ||
              driver.carType === "GREEN" ||
              driver.carType === "VAN"
            ) {
              counts.delivery.car++;
            }
          }
          counts.normal.total++;
          if (driver.carType === "CLASSIC") {
            counts.normal.classic++;
          } else if (driver.carType === "GREEN") {
            counts.normal.green++;
          } else if (driver.carType === "VAN") {
            counts.normal.van++;
          }
        }
      } else {
        counts.normal.total++;
        if (driver?.carType === "CLASSIC") {
          counts.normal.classic++;
        } else if (driver?.carType === "GREEN") {
          counts.normal.green++;
        } else if (driver?.carType === "VAN") {
          counts.normal.van++;
        }
      }
    });

    return counts;
  }, [driverLocations, drivers]);

  return (
    <Box sx={{ height: "600px", width: "100%", position: "relative" }}>
      <style>{styles}</style>
      <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY!}>
        <Map
          key={selectedRegion?.region_code || "default"}
          defaultCenter={mapCenter}
          defaultZoom={12}
          gestureHandling={"greedy"}
          disableDefaultUI={false}
        >
          <>
            {driverLocations.length === 0 ? (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  padding: "20px",
                  borderRadius: "4px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  zIndex: 1000,
                }}
              >
                <Typography>No drivers currently available on the map</Typography>
              </Box>
            ) : (
              <>
                {driverLocations.map((driverLoc) => {
                  const driver = drivers.find((d) => d._id === driverLoc.driverId);
                  if (!driver) return null;

                  // Check if driver is online (in connectedClients) or offline (has lastLocationUpdated)
                  const isOnline = connectedClients.some((client) => client.clientId === driver._id);

                  return (
                    <CustomMarker
                      key={driverLoc.driverId}
                      position={driverLoc.location}
                      label={`${driver.firstName} ${driver.lastName}`}
                      color={driverLoc.color}
                      onClick={() => setSelectedDriver(driver)}
                      driver={driver}
                      isOnline={isOnline}
                    />
                  );
                })}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "20px",
                    left: "20px",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    padding: "10px",
                    borderRadius: "4px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    zIndex: 1000,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Driver Types:
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Typography variant="body2">
                        Livreur ({getDriverTypeCounts().delivery.total})
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, pl: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <img src={normalBike} alt="Bike" style={{ width: 20, height: 20 }} />
                          <Typography variant="body2">
                            ({getDriverTypeCounts().delivery.bike})
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <img src={normalTruck} alt="Truck" style={{ width: 20, height: 20 }} />
                          <Typography variant="body2">
                            ({getDriverTypeCounts().delivery.truck})
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <img src={normalCar} alt="Car" style={{ width: 20, height: 20 }} />
                          <Typography variant="body2">
                            ({getDriverTypeCounts().delivery.car})
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Typography variant="body2">
                        Covoiturage ({getDriverTypeCounts().professional.total})
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, pl: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <img
                            src={normalSharedCar}
                            alt="Classic"
                            style={{ width: 20, height: 20 }}
                          />
                          <Typography variant="body2">
                            Classic ({getDriverTypeCounts().professional.classic})
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <img
                            src={normalSharedCar}
                            alt="Green"
                            style={{ width: 20, height: 20 }}
                          />
                          <Typography variant="body2">
                            Comfort ({getDriverTypeCounts().professional.green})
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <img src={normalSharedCar} alt="Van" style={{ width: 20, height: 20 }} />
                          <Typography variant="body2">
                            Van ({getDriverTypeCounts().professional.van})
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Typography variant="body2">
                        VTC & Taxi ({getDriverTypeCounts().normal.total})
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, pl: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <img src={normalCar} alt="Classic" style={{ width: 20, height: 20 }} />
                          <Typography variant="body2">
                            Classic ({getDriverTypeCounts().normal.classic})
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <img src={normalCar} alt="Green" style={{ width: 20, height: 20 }} />
                          <Typography variant="body2">
                            Comfort ({getDriverTypeCounts().normal.green})
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <img src={normalVan} alt="Van" style={{ width: 20, height: 20 }} />
                          <Typography variant="body2">
                            Van ({getDriverTypeCounts().normal.van})
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {selectedDriver && (
              <InfoWindow
                position={
                  driverLocations.find((dl) => dl.driverId === selectedDriver._id)?.location ||
                  mapCenter
                }
                onCloseClick={() => setSelectedDriver(null)}
              >
                <Box
                  sx={{
                    p: 2,
                    minWidth: "250px",
                    "& .MuiTypography-root": {
                      fontFamily: "Roboto, Arial, sans-serif",
                    },
                  }}
                >
                  {/* Driver Name and Type */}
                  <Box
                    sx={{
                      borderBottom: "2px solid #f0f0f0",
                      pb: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#1a1a1a",
                        mb: 0.5,
                      }}
                    >
                      {`${selectedDriver.firstName} ${selectedDriver.lastName}`}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {selectedDriver.driverType === "professional"
                        ? "Shared Driver"
                        : textCapitalise(selectedDriver.driverType)}{" "}
                      Driver
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      Accept Shared Ride: {selectedDriver.accept_shared_rides ? "Yes" : "No"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      Accept Deliveries: {selectedDriver.accept_deliveries ? "Yes" : "No"}
                    </Typography>
                    {selectedDriver.lastLocationUpdated && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#999",
                          fontSize: "0.75rem",
                          fontStyle: "italic",
                          mt: 0.5,
                        }}
                      >
                        Last Location: {(() => {
                          try {
                            const date = new Date(selectedDriver.lastLocationUpdated);
                            const now = new Date();
                            const diffMs = now.getTime() - date.getTime();
                            const diffMins = Math.floor(diffMs / 60000);
                            const diffHours = Math.floor(diffMins / 60);
                            const diffDays = Math.floor(diffHours / 24);

                            if (diffMins < 1) return "Just now";
                            if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
                            if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
                            if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
                            return date.toLocaleString();
                          } catch (e) {
                            return "Unknown";
                          }
                        })()}
                      </Typography>
                    )}
                  </Box>

                  {/* Contact Information */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#666",
                        mb: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Contact Information
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        pl: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "#333",
                        }}
                      >
                        <span style={{ color: "#666", minWidth: "80px" }}>Phone:</span>
                        {selectedDriver.phone || "N/A"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "#333",
                        }}
                      >
                        <span style={{ color: "#666", minWidth: "80px" }}>Email:</span>
                        {selectedDriver.email || "N/A"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Vehicle Information */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#666",
                        mb: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Vehicle Information
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        pl: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "#333",
                        }}
                      >
                        <span style={{ color: "#666", minWidth: "80px" }}>Car Type:</span>
                        {selectedDriver.carType
                          ? selectedDriver.address?.country === "CI" &&
                            selectedDriver.carType === "GREEN"
                            ? "COMFORT"
                            : selectedDriver.carType
                          : "N/A"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Ride Statistics */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "#666",
                        mb: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Ride Statistics
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1,
                        pl: 1,
                      }}
                    >
                      <Box
                        sx={{
                          p: 1,
                          backgroundColor: "#f5f5f5",
                          borderRadius: "4px",
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                          Total Rides
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color: "#1a1a1a",
                            fontWeight: 600,
                          }}
                        >
                          {selectedDriver.totalRideCount || 0}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: 1,
                          backgroundColor: "#f5f5f5",
                          borderRadius: "4px",
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                          Rejected Rides
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color:
                              selectedDriver.rejectedRideCount &&
                              selectedDriver.rejectedRideCount > 0
                                ? "#d32f2f"
                                : "#1a1a1a",
                            fontWeight: 600,
                          }}
                        >
                          {selectedDriver.rejectedRideCount || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </InfoWindow>
            )}
          </>
        </Map>
      </APIProvider>
    </Box>
  );
};

const WebSocketComponent: React.FC = () => {
  const [connectedClients, setConnectedClients] = useState<Client[]>([]);
  const [disconnectedClients, setDisconnectedClients] = useState<Client[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [allDrivers, setAllDrivers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(Date.now());
  const [adminCountry, setAdminCountry] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string | "">("all");
  const [driverType, setDriverType] = useState<string | "">("all");
  const [carType, setCarType] = useState<string | "">("all");
  const [minRejectedRides, setMinRejectedRides] = useState<number | "">("");
  const [maxRejectedRides, setMaxRejectedRides] = useState<number | "">("");
  const [minRides, setMinRides] = useState<number | "">("");
  const [maxRides, setMaxRides] = useState<number | "">("");
  const [country, setCountry] = useState<string | "">("all");
  const [city, setCity] = useState<string | "">("all");
  const [driverPlan, setDriverPlan] = useState<string | "">("all");
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "map">("map");
  const [userRole, setUserRole] = useState<string>("");

  // New state for dynamic country data
  const [countryData, setCountryData] = useState<any[]>([]);
  const [availableRegions, setAvailableRegions] = useState<any[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<any>(null);

  // Function to fetch country data
  const fetchCountryData = async () => {
    try {
      const data = await getCountriesData();
      setCountryData(data);
    } catch (error) {
      console.error("Error fetching country data:", error);
    }
  };

  // Function to update available regions when country changes
  const updateAvailableRegions = (selectedCountry: string) => {
    if (selectedCountry === "all") {
      setAvailableRegions([]);
      setSelectedRegion(null);
      return;
    }

    const countryInfo = countryData.find((c: any) => c.country_code === selectedCountry);
    if (countryInfo && countryInfo.regions) {
      setAvailableRegions(countryInfo.regions);
      // Auto-select the first region when country changes
      if (countryInfo.regions.length > 0) {
        setSelectedRegion(countryInfo.regions[0]);
        setCity(countryInfo.regions[0].region_code);
      }
    } else {
      setAvailableRegions([]);
      setSelectedRegion(null);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded: any = jwtDecode(token);
      setUserRole(decoded.role);
    }
  }, []);

  // Fetch country data on component mount
  useEffect(() => {
    fetchCountryData();
  }, []);

  // Update available regions when country changes
  useEffect(() => {
    updateAvailableRegions(country);
  }, [country, countryData]);

  // Update selected region when city changes
  useEffect(() => {
    if (city !== "all") {
      const region = availableRegions.find((r: any) => r.region_code === city);
      setSelectedRegion(region || null);
    } else {
      setSelectedRegion(null);
    }
  }, [city, availableRegions]);

  const setConnectedClientsWithTime = (connections: Client[]) => {
    // Create an object to store unique clients by clientId
    const uniqueClients: { [key: string]: Client } = {};

    // Keep only the most recent client data for each clientId
    connections?.forEach((client) => {
      const existingClient = uniqueClients[client.clientId];
      if (
        !existingClient ||
        (client.connectedTime &&
          (!existingClient.connectedTime ||
            new Date(client.connectedTime) > new Date(existingClient.connectedTime)))
      ) {
        uniqueClients[client.clientId] = {
          ...client,
          connectedTime: client.connectedTime || new Date().toLocaleTimeString(),
        };
      }
    });

    setConnectedClients(Object.values(uniqueClients));
    setLastUpdateTime(Date.now());
  };

  const setDisconnectedClientsWithTime = (connection: Client) => {
    const updatedClient = {
      ...connection,
      connectedTime: new Date().toLocaleTimeString(),
    };

    setDisconnectedClients((prevClients) => {
      // Create an object to store unique clients by clientId
      const uniqueClients: { [key: string]: Client } = {};

      // Add all previous clients to the object
      prevClients.forEach((client) => {
        uniqueClients[client.clientId] = client;
      });

      // Add or update the new disconnected client
      uniqueClients[updatedClient.clientId] = updatedClient;

      return Object.values(uniqueClients);
    });

    setLastUpdateTime(Date.now());
  };

  const loadAllDrivers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const decoded: any = jwtDecode(token);
        setAdminCountry(decoded.countryCode);

        let countryCode = "";
        if (decoded.countryCode !== "all") {
          countryCode =
            decoded.countryCode === "uk"
              ? "GB"
              : decoded.countryCode === "france"
              ? "FR"
              : decoded.countryCode === "ivory-coast"
              ? "CI"
              : "";
        }

        // Use the new endpoint that includes location data
        const data: UserResponse = await userService.getDriversWithLocations({
          page: 1,
          limit: 10000,
          status: "approved",
          country: countryCode,
        });
        setAllDrivers(data.users);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllDrivers();
  }, []);

  useEffect(() => {
    const API_KEY = process.env.REACT_APP_API_KEY;
    const socketBaseUrl = process.env.REACT_APP_WEB_SOCKET_SERVICE_BASE_URL;
    console.log("API Key: " + API_KEY);

    let socket: WebSocket | null = null;
    let reconnectTimeout: NodeJS.Timeout;
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 5;
    const RECONNECT_DELAY = 5000; // 5 seconds

    const connectWebSocket = () => {
      if (socket?.readyState === WebSocket.OPEN) {
        console.log("WebSocket already connected");
        return;
      }

      socket = new WebSocket(`${socketBaseUrl}?api_key=${API_KEY}`);

      socket.onopen = () => {
        console.log("WebSocket connection opened");
        reconnectAttempts = 0; // Reset reconnect attempts on successful connection
      };

      let messageQueue: Event[] = [];
      let processingQueue = false;
      let lastProcessTime = 0;
      const PROCESS_INTERVAL = 120000; // 2 minutes delay between processing

      const processMessageQueue = () => {
        if (processingQueue || messageQueue.length === 0) return;

        const now = Date.now();
        const timeSinceLastProcess = now - lastProcessTime;

        if (timeSinceLastProcess < PROCESS_INTERVAL) {
          // If not enough time has passed, schedule next check
          setTimeout(processMessageQueue, PROCESS_INTERVAL - timeSinceLastProcess);
          return;
        }

        processingQueue = true;
        const data = messageQueue.shift();

        if (data) {
          console.log("Processing queued message:", data.event, "Queue size:", messageQueue.length);
          switch (data.event) {
            case "connect":
              setConnectedClientsWithTime(JSON.parse(data.connections));
              break;
            case "disconnect":
              setDisconnectedClientsWithTime(data.client);
              setConnectedClientsWithTime(JSON.parse(data.connections));
              break;
            case "error":
              setErrors((prevErrors) => [
                ...prevErrors,
                `${data.error} - ${new Date().toLocaleTimeString()}`,
              ]);
              setConnectedClientsWithTime(JSON.parse(data.connections));
              break;
          }
          lastProcessTime = now;
        }

        processingQueue = false;
        if (messageQueue.length > 0) {
          // Schedule next processing after the interval
          setTimeout(processMessageQueue, PROCESS_INTERVAL);
        }
      };

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data) as Event;
        console.log("Message received", data.event, "at", new Date().toLocaleTimeString());

        // Add message to queue
        messageQueue.push(data);

        // Process queue if not already processing
        if (!processingQueue) {
          processMessageQueue();
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      socket.onclose = (event) => {
        console.log("WebSocket connection closed", event.code, event.reason);

        // Attempt to reconnect if not closed cleanly
        if (event.code !== 1000 && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts++;
          console.log(
            `Attempting to reconnect (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})...`
          );
          reconnectTimeout = setTimeout(connectWebSocket, RECONNECT_DELAY);
        } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          console.log("Max reconnection attempts reached. Please refresh the page manually.");
          setErrors((prev) => [
            ...prev,
            "WebSocket connection lost. Please refresh the page to reconnect.",
          ]);
        }
      };
    };

    // Initial connection
    connectWebSocket();

    // Cleanup on component unmount
    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (socket) {
        socket.close(1000, "Component unmounting");
      }
    };
  }, []);

  useEffect(() => {
    if (lastUpdateTime > 0) {
      loadAllDrivers();
    }
  }, [lastUpdateTime]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getOnlineDrivers = () => {
    const onlineDriverIds = new Set(connectedClients.map((client) => client.clientId));
    return allDrivers.filter((driver) => onlineDriverIds.has(driver._id));
  };

  const getOfflineDrivers = () => {
    const onlineDriverIds = new Set(connectedClients.map((client) => client.clientId));
    return allDrivers.filter((driver) => !onlineDriverIds.has(driver._id));
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const downloadPDF = (drivers: User[], title: string) => {
    const doc = new jsPDF();
    doc.text(title, 14, 15);

    const tableColumn = ["Name", "Email", "Phone", "Car Type", "Driver Type", "Status"];
    const tableRows = drivers.map((driver) => [
      `${driver.firstName} ${driver.lastName}`,
      driver.email || "-",
      driver.phone || "-",
      driver.carType || "-",
      driver.driverType ? getDriverTypes(driver) : "-",
      driver.vtcStatus ? formatTextToCapitalise(driver.vtcStatus) : "-",
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 25,
      theme: "grid",
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(
      `${title.toLowerCase().replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleFilterChange =
    (setter: React.Dispatch<React.SetStateAction<any>>) => (value: any) => {
      setter(value);
    };

  const filterDrivers = (drivers: User[]) => {
    return drivers.filter((driver) => {
      // Search filter
      const searchLower = search.toLowerCase();
      const matchesSearch =
        search === "" ||
        `${driver.firstName} ${driver.lastName}`.toLowerCase().includes(searchLower) ||
        (driver.email || "").toLowerCase().includes(searchLower) ||
        (driver.phone || "").toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus = status === "all" || driver.vtcStatus === status;

      // Driver type filter
      const matchesDriverType = driverType === "all" || driver.driverType === driverType;

      // Car type filter
      const matchesCarType = carType === "all" || driver.carType === carType;

      // Rejected rides filter
      const rejectedRides = driver.rejectedRideCount || 0;
      const matchesRejectedRides =
        (minRejectedRides === "" || rejectedRides >= minRejectedRides) &&
        (maxRejectedRides === "" || rejectedRides <= maxRejectedRides);

      // Total rides filter
      const totalRides = driver.totalRideCount || 0;
      const matchesTotalRides =
        (minRides === "" || totalRides >= minRides) && (maxRides === "" || totalRides <= maxRides);

      // Driver plan filter
      const matchesDriverPlan =
        driverPlan === "all" ||
        (driverPlan === "is_premium" && driver.is_premium_vtc) ||
        (driverPlan === "is_community" && driver.is_community_vtc) ||
        (driverPlan === "normal" && !driver.is_premium_vtc && !driver.is_community_vtc);

      return (
        matchesSearch &&
        matchesStatus &&
        matchesDriverType &&
        matchesCarType &&
        matchesRejectedRides &&
        matchesTotalRides &&
        matchesDriverPlan
      );
    });
  };

  const renderFilters = () => (
    <Box>
      <TextField
        label="Search Drivers by name, email & phone number"
        value={search}
        onChange={handleSearchChange}
        fullWidth
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, mb: 2 }}>
        {/* Status Filter */}
        {/* <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            onChange={(e) => handleFilterChange(setStatus)(e.target.value)}
            label="Status"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="document_verified">Document Verified</MenuItem>
            <MenuItem value="bank_details_added">Bank Details Added</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl> */}

        {/* Driver Type Filter */}
        <FormControl fullWidth>
          <InputLabel>Driver Type</InputLabel>
          <Select
            value={driverType}
            onChange={(e) => handleFilterChange(setDriverType)(e.target.value)}
            label="Driver Type"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="professional">Shared Drivers</MenuItem>
            <MenuItem value="delivery">Delivery</MenuItem>
          </Select>
        </FormControl>

        {/* Car Type Filter */}
        <FormControl fullWidth>
          <InputLabel>Car Type</InputLabel>
          <Select
            value={carType}
            onChange={(e) => handleFilterChange(setCarType)(e.target.value)}
            label="Car Type"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="NOCAR">No Car</MenuItem>
            <MenuItem value="CLASSIC">Classic</MenuItem>
            <MenuItem value="GREEN">Green or Comfort for IC</MenuItem>
            <MenuItem value="VAN">Van</MenuItem>
            <MenuItem value="MOTOR_BIKE">Motor Bike</MenuItem>
            <MenuItem value="TRIKE">Trike/Van...</MenuItem>
            <MenuItem value="MEDIUM_TRUCK">{"Medium Truck (< 1.5 ton)"}</MenuItem>
            <MenuItem value="LARGE_TRUCK">{"Large Truck (> 1.5 Ton)"}</MenuItem>
          </Select>
        </FormControl>

        {/* Min & Max Rejected Rides */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Min Rejected Rides"
            value={minRejectedRides}
            onChange={(e) =>
              handleFilterChange(setMinRejectedRides)(e.target.value ? +e.target.value : "")
            }
            type="number"
            fullWidth
          />
          <TextField
            label="Max Rejected Rides"
            value={maxRejectedRides}
            onChange={(e) =>
              handleFilterChange(setMaxRejectedRides)(e.target.value ? +e.target.value : "")
            }
            type="number"
            fullWidth
          />
        </Box>

        {/* Min & Max Total Rides */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Min Total Rides"
            value={minRides}
            onChange={(e) => handleFilterChange(setMinRides)(e.target.value ? +e.target.value : "")}
            type="number"
            fullWidth
          />
          <TextField
            label="Max Total Rides"
            value={maxRides}
            onChange={(e) => handleFilterChange(setMaxRides)(e.target.value ? +e.target.value : "")}
            type="number"
            fullWidth
          />
        </Box>

        {/* Driver Plan Filter */}
        <FormControl fullWidth>
          <InputLabel>Driver Plan</InputLabel>
          <Select
            value={driverPlan}
            onChange={(e) => handleFilterChange(setDriverPlan)(e.target.value)}
            label="Driver Plan"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="is_premium">Premium</MenuItem>
            <MenuItem value="is_community">Community</MenuItem>
          </Select>
        </FormControl>

        {/* Country Filter */}
        {adminCountry === "all" && (
          <FormControl fullWidth>
            <InputLabel>Country</InputLabel>
            <Select
              value={country}
              onChange={(e) => handleFilterChange(setCountry)(e.target.value)}
              label="Country"
            >
              <MenuItem value="all">All</MenuItem>
              {countryData.map((countryInfo: any) => (
                <MenuItem key={countryInfo.country_code} value={countryInfo.country_code}>
                  {countryInfo.country_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* City Filter */}
        <FormControl fullWidth>
          <InputLabel>City</InputLabel>
          <Select
            value={city}
            onChange={(e) => handleFilterChange(setCity)(e.target.value)}
            label="City"
            disabled={availableRegions.length === 0}
          >
            <MenuItem value="all">All</MenuItem>
            {availableRegions.map((region: any) => (
              <MenuItem key={region.region_code} value={region.region_code}>
                {region.region_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );

  const renderDriverView = (drivers: User[], title: string) => {
    // Show "(Last Location)" suffix for offline drivers in map view
    const displayTitle = title === "Offline Drivers" && viewMode === "map" 
      ? "Offline Drivers (Last Location)" 
      : title;
    
    return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{displayTitle}</Typography>
        <Box>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            sx={{ mr: 2 }}
          >
            <ToggleButton value="table">Table View</ToggleButton>
            <ToggleButton value="map">Map View</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            onClick={() => setFiltersVisible((prev) => !prev)}
            sx={{ mr: 1 }}
          >
            {filtersVisible ? "Hide Filters" : "Show Filters"}
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => downloadPDF(filterDrivers(drivers), title)}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      {filtersVisible && renderFilters()}

      {viewMode === "table" ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead style={{ backgroundColor: "rgb(224, 224, 224)" }}>
              <TableRow>
                <TableCell>Client ID</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Driver Name</TableCell>
                <TableCell>Car Type</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Driver Type</TableCell>
                <TableCell>Is Teracar Driver?</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Total Rides</TableCell>
                <TableCell>Rejected Rides</TableCell>
                <TableCell>Driver Plan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filterDrivers(drivers).map((driver) => (
                <TableRow key={driver._id}>
                  <TableCell>{driver._id}</TableCell>
                  <TableCell>driver</TableCell>
                  <TableCell>{`${driver.firstName} ${driver.lastName}` || "-"}</TableCell>
                  <TableCell>
                    {driver.address?.country === "CI" && driver.carType === "GREEN"
                      ? "COMFORT"
                      : driver.carType
                      ? driver.carType
                      : "-"}
                  </TableCell>
                  <TableCell>{driver.email || "-"}</TableCell>
                  <TableCell>{driver.phone || "-"}</TableCell>
                  <TableCell>{driver.driverType ? getDriverTypes(driver) : "Normal"}</TableCell>
                  <TableCell>{driver.is_teracar_driver ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    {driver.vtcStatus ? formatTextToCapitalise(driver.vtcStatus) : "-"}
                  </TableCell>
                  <TableCell>{driver.totalRideCount || 0}</TableCell>
                  <TableCell>{driver.rejectedRideCount || 0}</TableCell>
                  <TableCell>
                    {driver.is_premium_vtc
                      ? "Premium"
                      : driver.is_community_vtc
                      ? "Community"
                      : "Normal"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <MapView
          drivers={filterDrivers(drivers)}
          connectedClients={connectedClients}
          selectedRegion={selectedRegion}
        />
      )}
    </Box>
  );
  };

  return (
    <Box className="mainContainer">
      <Box className="sidePanelContainer">
        <SidePanel isOpen={true} onClose={() => {}} />
      </Box>
      <Box className="dashboardContainer">
        <TopBar />
        <Box style={{ width: "100%", padding: "20px", marginTop: "5%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="WebSocket Status" />
              <Tab label="Online Drivers" />
              <Tab label="Offline Drivers" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
              <Paper elevation={3} style={{ margin: "20px", padding: "20px" }}>
                <Typography variant="h5">Connected Clients (Non-Drivers)</Typography>
                <TableContainer>
                  <Table>
                    <TableHead style={{ backgroundColor: "rgb(224, 224, 224)" }}>
                      <TableRow>
                        <TableCell>Client ID</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Connected Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {connectedClients
                        .filter((client) => client.clientRole !== "DRIVER")
                        .map((client) => (
                          <TableRow key={client.clientId}>
                            <TableCell>{client.clientId}</TableCell>
                            <TableCell>{client.clientRole}</TableCell>
                            <TableCell>{client.ownerName || "-"}</TableCell>
                            <TableCell>{client.email || "-"}</TableCell>
                            <TableCell>{client.phoneNumber || "-"}</TableCell>
                            <TableCell>{client.connectedTime || "-"}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Paper elevation={3} style={{ margin: "20px", padding: "20px" }}>
                <Typography variant="h5">Disconnected Clients (Non-Drivers)</Typography>
                <TableContainer>
                  <Table>
                    <TableHead style={{ backgroundColor: "rgb(224, 224, 224)" }}>
                      <TableRow>
                        <TableCell>Client ID</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Disconnected Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {disconnectedClients
                        .filter((client) => client.clientRole !== "DRIVER")
                        .map((client) => (
                          <TableRow key={client.clientId}>
                            <TableCell>{client.clientId}</TableCell>
                            <TableCell>{client.clientRole}</TableCell>
                            <TableCell>{client.ownerName || "-"}</TableCell>
                            <TableCell>{client.email || "-"}</TableCell>
                            <TableCell>{client.phoneNumber || "-"}</TableCell>
                            <TableCell>{client.connectedTime || "-"}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Paper elevation={3} style={{ margin: "20px", padding: "20px" }}>
                <Typography variant="h5">Errors</Typography>
                <List>
                  {errors.map((error, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={`${error}`} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography>Loading drivers...</Typography>
              </Box>
            ) : (
              renderDriverView(getOnlineDrivers(), "Online Drivers")
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography>Loading drivers...</Typography>
              </Box>
            ) : (
              renderDriverView(getOfflineDrivers(), "Offline Drivers")
            )}
          </TabPanel>
        </Box>
      </Box>
    </Box>
  );
};

export default WebSocketComponent;
